import { useNavigate } from 'react-router';
import { Globe, Plus, Search, Lock, Unlock, ArrowRight, Hourglass, CreditCard } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  StatusBadge,
  StatusCell,
  DateCell,
  ActionsMenuCell,
  type StatusTone,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useDomains, type Domain, type DomainStatus } from '@/domains/domains-registry';
import { usePendingTransferOrders } from './transfer/usePendingTransferOrders';

const STATUS_LABEL: Record<DomainStatus, string> = {
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  pending: 'Pending',
  'transfer-in': 'Transfer in',
  'transfer-out': 'Transfer out',
  suspended: 'Suspended',
};

const STATUS_TONE: Record<DomainStatus, StatusTone> = {
  active: 'active',
  expiring: 'expiring',
  expired: 'overdue',
  pending: 'pending',
  'transfer-in': 'info',
  'transfer-out': 'info',
  suspended: 'failed',
};

export default function DomainsListPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDomains();

  /**
   * STOCK. `Expires`, not "Created" — what a customer asks their own domain
   * list is "what runs out next", the same reasoning as the admin list.
   */
  const table = useTableFilters<Domain>({
    rows: data,
    dateMode: 'client',
    dateLabel: 'Expires',
    dateOf: (d) => d.expiresAt,
    searchText: (d) => d.name,
  });
  const pending = usePendingTransferOrders();

  const columns: UniversalTableColumn<Domain>[] = [
    {
      key: 'name',
      header: 'Domain',
      sortValue: (d) => d.name,
      render: (d) => (
        <span className="inline-flex items-center gap-2 font-medium text-ink">
          <Globe className="h-4 w-4 text-muted-foreground" />
          {d.name}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (d) => d.status,
      render: (d) => <StatusCell tone={STATUS_TONE[d.status]} label={STATUS_LABEL[d.status]} />,
      width: 'w-32',
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      sortValue: (d) => d.expiresAt,
      render: (d) => <DateCell value={d.expiresAt} format="long" />,
      hideOnMobile: true,
    },
    {
      key: 'autoRenew',
      header: 'Auto-renew',
      sortValue: (d) => (d.autoRenew ? 1 : 0),
      render: (d) => (
        <StatusBadge tone={d.autoRenew ? 'active' : 'draft'}>
          {d.autoRenew ? 'On' : 'Off'}
        </StatusBadge>
      ),
      hideOnMobile: true,
      width: 'w-32',
    },
    {
      key: 'registryLocked',
      header: 'Lock',
      render: (d) => (
        <StatusBadge tone={d.registryLocked ? 'active' : 'pending'}>
          {d.registryLocked ? (
            <span className="inline-flex items-center gap-1">
              <Lock className="size-3" aria-hidden /> Locked
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <Unlock className="size-3" aria-hidden /> Unlocked
            </span>
          )}
        </StatusBadge>
      ),
      hideOnMobile: true,
      width: 'w-32',
    },
    {
      key: 'actions',
      header: '',
      render: (d) => (
        <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
          <ActionsMenuCell
            ariaLabel={`Actions for ${d.name}`}
            items={[
              {
                label: 'Open domain',
                onSelect: () => navigate(`/app/domains/${encodeURIComponent(d.name)}`),
              },
              {
                label: 'DNS records',
                onSelect: () => navigate(`/app/domains/${encodeURIComponent(d.name)}/dns`),
              },
              {
                label: 'Nameservers',
                onSelect: () => navigate(`/app/domains/${encodeURIComponent(d.name)}/nameservers`),
              },
              {
                label: d.autoRenew ? 'Disable auto-renew' : 'Enable auto-renew',
                onSelect: () => navigate(`/app/domains/${encodeURIComponent(d.name)}?action=auto-renew`),
                separated: true,
              },
              {
                label: 'Transfer out',
                tone: 'danger',
                onSelect: () => navigate(`/app/domains/${encodeURIComponent(d.name)}/transfer`),
              },
            ]}
          />
        </div>
      ),
      width: 'w-12',
      align: 'right',
    },
  ];

  return (
    <UniversalPage
      title="Domains"
      description="Domains you have registered or transferred to SightHost."
      breadcrumbs={[{ label: 'Home', href: '/app' }, { label: 'Domains' }]}
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/app/domains/whois')}
          >
            <Search className="h-4 w-4" />
            WHOIS lookup
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/app/domains/transfer')}
          >
            <ArrowRight className="h-4 w-4" />
            Transfer in
          </Button>
          <Button size="sm" onClick={() => navigate('/app/domains/register')}>
            <Plus className="h-4 w-4" />
            Register domain
          </Button>
        </>
      }
    >
      {pending.data.length > 0 && (
        <PendingTransfersStrip
          orders={pending.data}
          onTrack={(orderId) => navigate(`/app/domains/transfer/${orderId}`)}
          onPay={(invoiceId) => navigate(`/app/billing/invoices/${invoiceId}`)}
        />
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <PeriodFilter
          value={table.filters.period}
          from={table.filters.from}
          to={table.filters.to}
          onChange={table.filters.setPeriod}
          onStep={table.stepPeriodBy}
          presets={TABLE_PRESETS}
          variant="menu"
          steppable
          label="Expires"
        />
        <SearchField
          value={table.q}
          onValueChange={table.setQ}
          pending={table.isSearching}
          placeholder="Search domain name"
          className="ml-auto w-full max-w-xs"
        />
      </div>
      <UniversalTable
        data={table.rows}
        columns={columns}
        getRowKey={(d) => d.id}
        caption={!isLoading && !error ? table.caption : undefined}
        onRowClick={(d) => navigate(`/app/domains/${encodeURIComponent(d.name)}`)}
        isLoading={isLoading}
        error={error}
        empty={{
          icon: Globe,
          title: 'No domains yet',
          description: 'Register a new domain or transfer one in to get started.',
          action: (
            <Button size="sm" onClick={() => navigate('/app/domains/register')}>
              <Plus className="h-4 w-4" />
              Register a domain
            </Button>
          ),
        }}
      />
    </UniversalPage>
  );
}

// ─── Pending transfers strip ──────────────────────────────────────────

interface StripProps {
  orders: ReturnType<typeof usePendingTransferOrders>['data'];
  onTrack: (orderId: string) => void;
  onPay: (invoiceId: string) => void;
}

function PendingTransfersStrip({ orders, onTrack, onPay }: StripProps) {
  return (
    <Card interactive={false} className="mb-6 border-warning/30 bg-warning/10">
      <header className="border-b border-warning/30 px-5 py-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-warning-dark inline-flex items-center gap-2">
          <Hourglass className="size-3.5" aria-hidden />
          Transfers in progress ({orders.length})
        </h3>
      </header>
      <ul className="divide-y divide-warning/30">
        {orders.map((o) => {
          const awaitingPayment = o.status === 'pending_payment' && o.invoice_lifecycle_state !== 'paid';
          return (
            <li key={o.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink tabular-nums">
                  {o.domain_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {awaitingPayment
                    ? `Awaiting payment · invoice for R${(o.invoice_total ?? 0).toFixed(2)}`
                    : 'Awaiting registry confirmation · 5–7 days typical'}
                  {' · '}
                  Started {new Date(o.created_at).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' })}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                {awaitingPayment && o.invoice_id && (
                  <Button size="sm" variant="outline" onClick={() => onPay(o.invoice_id!)}>
                    <CreditCard className="size-3.5" />
                    Pay invoice
                  </Button>
                )}
                <Button size="sm" onClick={() => onTrack(o.id)}>
                  Track status
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
